<template>
  <div class="pt-4">
    <!-- Quick Navigation Menu -->
    <b-row class="mb-4 position-sticky quick-nav-menu-wrapper">
      <b-col cols="12" offset-sm="1" sm="10">
        <div class="quick-nav-menu">
          <div class="quick-nav-links">
            <a
              v-for="(section, idx) in sektionen"
              :key="`nav-${idx}`"
              :href="`#section-${idx}`"
              class="quick-nav-link"
              @click.prevent="scrollToSection(idx)"
            >
              {{ section.title }}
            </a>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row
      v-for="(themeConfigSection, sectionIndex) in sektionen"
      :key="`sec${sectionIndex}`"
      :id="`section-${sectionIndex}`"
      class="mb-4 section-anchor"
    >
      <b-col cols="12" offset-sm="1" sm="10" class="sticky-title">
        <h3>
          {{ themeConfigSection.title }}
        </h3>
        <hr>
      </b-col>
      <b-col>
        <div
          v-for="(themeConfigItem, index) in themeConfigSection.fields"
          :key="index"
          :class="{ 'conditional-field': isSecondLevelField(themeConfigItem) }"
        >
          <component
            v-if="showComponent(themeConfigItem)"
            :is="themeConfigItem.fieldType"
            :handle="themeConfigItem.handle"
            :label="themeConfigItem.title"
            :desc="themeConfigItem.desc"
            :checkboxLabel="themeConfigItem.checkboxLabel"
            :options="themeConfigItem.options"
            :value-from-db="getComponentValue(themeConfigSection.handle, themeConfigItem)"
            :margin-bottom="themeConfigItem.marginBottom"
            :disabled="isDisabled(themeConfigItem)"
            :project-id="projectId"
            :class="{ 'disabled-field': isDisabled(themeConfigItem) }"
            @update-field="updateSettingValue($event, themeConfigItem.handle, themeConfigSection.handle)"
            @file-removed="updateSettingValue($event, themeConfigItem.handle, themeConfigSection.handle)"
          />
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import ImageUploadRow from '@/components/helper/ImageUploadRow.vue';
import CheckboxRow from '@/components/helper/CheckboxRow.vue';
import CheckboxRowWithLabel from '@/components/helper/CheckboxRowWithLabel.vue';
import ColorPickerRow from '@/components/helper/ColorPickerRow.vue';
import InputRowNoLabel from '@/components/helper/InputRowNoLabel.vue';
import InputRow from '@/components/helper/InputRow.vue';
import SelectRow from '@/components/helper/SelectRow.vue';
import NavigationLinks from '@/components/helper/NavigationLinks.vue';
import CustomButtonRow from '@/components/helper/CustomButtonRow.vue';
import InternalPageSelectRow from '@/components/helper/InternalPageSelectRow.vue';
import BottomNavPreview from '@/components/helper/BottomNavPreview.vue';
import themeSettingsDirectTop2 from '@/defaultData/themeSettingsDirectTop2';
import cloneDeep from 'lodash/cloneDeep';
import { v3Sektionen } from '@/utils/themeV3';
import userDataAndAccess from '@/mixins/userDataAndAccess';

export default {
  name: 'ThemeSettingsDirectTop2',
  mixins: [userDataAndAccess],
  components: {
    CheckboxRow,
    CheckboxRowWithLabel,
    ImageUploadRow,
    InputRowNoLabel,
    InputRow,
    SelectRow,
    ColorPickerRow,
    NavigationLinks,
    CustomButtonRow,
    InternalPageSelectRow,
    BottomNavPreview,
  },
  props: {
    settingValues: {
      type: Object,
      default: null,
    },
    projectId: {
      type: Number,
      default: null,
    },
    // Theme v3 (spec 2026-10-08-theme-v3-design): same settings, without the parts v3 does not render.
    nurV3: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      themeFieldConfig: [
        {
          title: this.$t('settings.project.projectType.themeSetting.navigation.menu.title'),
          handle: 'menu',
          fields: [
            {
              handle: 'logoimage',
              title: this.$t('settings.project.projectType.themeSetting.navigation.logoimage.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.logoimage.desc'),
              fieldType: 'ImageUploadRow',
            },
            {
              handle: 'hideMenu',
              title: this.$t('settings.project.projectType.themeSetting.navigation.hideMenu.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.hideMenu.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.hideMenu.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'show_logo',
              title: this.$t('settings.project.projectType.themeSetting.navigation.show_logo.title'),
              desc: null,
              fieldType: 'CheckboxRow',
            },
            {
              handle: 'title',
              title: this.$t('settings.project.projectType.themeSetting.navigation.title.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.title.desc'),
              fieldType: 'InputRow',
            },
            {
              handle: 'article',
              title: this.$t('settings.project.projectType.themeSetting.navigation.article.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.article.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.article.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'pagination',
              title: this.$t('settings.project.projectType.themeSetting.navigation.pagination.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.pagination.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.pagination.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'shadow',
              title: this.$t('settings.project.projectType.themeSetting.navigation.shadow.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.shadow.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.shadow.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'show_borders',
              title: this.$t('settings.project.projectType.themeSetting.navigation.show_borders.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.show_borders.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.show_borders.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'background_color',
              title: this.$t('settings.project.projectType.themeSetting.navigation.background_color.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.background_color.desc'),
              fieldType: 'ColorPickerRow',
            },
            {
              handle: 'color',
              title: this.$t('settings.project.projectType.themeSetting.navigation.color.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.color.desc'),
              fieldType: 'ColorPickerRow',
            },
            {
              handle: 'scrollMenu',
              title: this.$t('settings.project.projectType.themeSetting.navigation.scrollMenu.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.scrollMenu.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.scrollMenu.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'menuTags',
              title: this.$t('settings.project.projectType.themeSetting.navigation.menuTags.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.menuTags.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.menuTags.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'modalNavigation',
              title: this.$t('settings.project.projectType.themeSetting.navigation.modalNavigation.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.modalNavigation.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.modalNavigation.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'modalDisplay',
              title: this.$t('settings.project.projectType.themeSetting.navigation.modalDisplay.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.modalDisplay.desc'),
              fieldType: 'SelectRow',
              options: [
                { value: null, text: this.$t('settings.project.projectType.themeSetting.navigation.modalDisplay.center') },
                { value: 'sheet', text: this.$t('settings.project.projectType.themeSetting.navigation.modalDisplay.sheet') },
              ],
            },
            {
              handle: 'hideNavOnScroll',
              title: this.$t('settings.project.projectType.themeSetting.navigation.hideNavOnScroll.title'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.hideNavOnScroll.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'hideNavScrollOnlyOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.hideNavScrollOnlyOnMobile.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.hideNavScrollOnlyOnMobile.desc'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menu.hideNavOnScroll',
            },
            {
              handle: 'menu_right',
              title: this.$t('settings.project.projectType.themeSetting.navigation.menu_right.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.menu_right.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.menu_right.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'navigation_background',
              title: this.$t('settings.project.projectType.themeSetting.navigation.navigation_background_color.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.navigation_background_color.desc'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menu.menu_right',
            },
            {
              handle: 'navigation_background_icon_color',
              desc: this.$t('settings.project.projectType.themeSetting.navigation.navigation_background_icon_color.desc'),
              fieldType: 'ColorPickerRow',
              showIfActive: ['menu.menu_right', 'menu.navigation_background'],
            },
          ],
        },
        {
          title: 'Menu Buttons',
          handle: 'menuButtons',
          fields: [
            {
              handle: 'mobile_dropdown',
              title: this.$t('settings.project.projectType.themeSetting.navigation.mobile_dropdown.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.mobile_dropdown.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.mobile_dropdown.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'activate_search',
              title: this.$t('settings.project.projectType.themeSetting.navigation.search.title'),
              desc: null,
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.search.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'search_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.search.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_search',
            },
            {
              handle: 'search_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.search_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.search_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_search',
            },
            {
              handle: 'activate_cookies',
              title: this.$t('settings.project.projectType.themeSetting.navigation.cookies.title'),
              desc: null,
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.cookies.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'cookies_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.cookies.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_cookies',
            },
            {
              handle: 'cookies_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.cookies_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.cookies_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_cookies',
            },
            {
              handle: 'activate_sharing',
              title: this.$t('settings.project.projectType.themeSetting.navigation.sharing.title'),
              desc: null,
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.sharing.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'sharing_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.sharing.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_sharing',
            },
            {
              handle: 'sharing_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.sharing_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.sharing_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_sharing',
            },

            {
              handle: 'activate_facebook',
              title: this.$t('settings.project.projectType.themeSetting.navigation.facebook.title'),
              desc: null,
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.facebook.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'facebook_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.facebook.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_facebook',
            },
            {
              handle: 'facebook',
              title: this.$t('settings.project.projectType.themeSetting.navigation.facebook.urlText'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.facebook.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_facebook',
            },
            {
              handle: 'facebook_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.facebook_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.facebook_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_facebook',
            },
            {
              handle: 'activate_instagram',
              title: this.$t('settings.project.projectType.themeSetting.navigation.instagram.title'),
              desc: null,
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.instagram.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'instagram_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.instagram.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_instagram',
            },
            {
              handle: 'instagram',
              title: this.$t('settings.project.projectType.themeSetting.navigation.instagram.urlText'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.instagram.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_instagram',
            },
            {
              handle: 'instagram_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.instagram_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.instagram_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_instagram',
            },
            {
              handle: 'activate_linkedin',
              title: this.$t('settings.project.projectType.themeSetting.navigation.linkedin.title'),
              desc: null,
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.linkedin.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'linkedin_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.linkedin.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_linkedin',
            },
            {
              handle: 'linkedin',
              title: this.$t('settings.project.projectType.themeSetting.navigation.linkedin.urlText'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.linkedin.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_linkedin',
            },
            {
              handle: 'linkedin_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.linkedin_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.linkedin_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_linkedin',
            },
            {
              handle: 'activate_twitter',
              title: this.$t('settings.project.projectType.themeSetting.navigation.twitter.title'),
              desc: null,
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.twitter.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'twitter_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.twitter.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_twitter',
            },
            {
              handle: 'twitter',
              title: this.$t('settings.project.projectType.themeSetting.navigation.twitter.urlText'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.twitter.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_twitter',
            },
            {
              handle: 'twitter_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.twitter_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.twitter_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_twitter',
            },
            {
              handle: 'activate_tiktok',
              title: this.$t('settings.project.projectType.themeSetting.navigation.tiktok.title'),
              desc: null,
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.tiktok.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'tiktok_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.tiktok.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_tiktok',
            },
            {
              handle: 'tiktok',
              title: this.$t('settings.project.projectType.themeSetting.navigation.tiktok.urlText'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.tiktok.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_tiktok',
            },
            {
              handle: 'tiktok_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.tiktok_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.tiktok_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_tiktok',
            },
            {
              handle: 'activate_bluesky',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bluesky.title'),
              desc: null,
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.bluesky.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'bluesky_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bluesky.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_bluesky',
            },
            {
              handle: 'bluesky',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bluesky.urlText'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bluesky.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_bluesky',
            },
            {
              handle: 'bluesky_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bluesky_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bluesky_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_bluesky',
            },
            {
              handle: 'activate_xing',
              title: this.$t('settings.project.projectType.themeSetting.navigation.xing.title'),
              desc: null,
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.xing.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'xing_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.xing.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_xing',
            },
            {
              handle: 'xing',
              title: this.$t('settings.project.projectType.themeSetting.navigation.xing.urlText'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.xing.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_xing',
            },
            {
              handle: 'xing_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.xing_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.xing_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_xing',
            },
            {
              handle: 'activate_youtube',
              title: this.$t('settings.project.projectType.themeSetting.navigation.youtube.title'),
              desc: null,
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.youtube.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'youtube_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.youtube.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_youtube',
            },
            {
              handle: 'youtube',
              title: this.$t('settings.project.projectType.themeSetting.navigation.youtube.urlText'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.youtube.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_youtube',
            },
            {
              handle: 'youtube_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.youtube_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.youtube_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_youtube',
            },
            {
              handle: 'activate_website',
              title: this.$t('settings.project.projectType.themeSetting.navigation.website.title'),
              desc: null,
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.website.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'website_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.website.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_website',
            },
            {
              handle: 'website',
              title: this.$t('settings.project.projectType.themeSetting.navigation.website.urlText'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.website.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_website',
            },
            {
              handle: 'website_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.website_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.website_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_website',
            },
            {
              handle: 'activate_print',
              title: this.$t('settings.project.projectType.themeSetting.navigation.print.title'),
              desc: null,
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.print.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'print_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.print.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_print',
            },
            {
              handle: 'print',
              title: this.$t('settings.project.projectType.themeSetting.navigation.print.urlText'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.print.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_print',
            },
            {
              handle: 'print_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.print_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.print_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_print',
            },
            {
              handle: 'activate_overview',
              title: this.$t('settings.project.projectType.themeSetting.navigation.overview.title'),
              desc: null,
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.overview.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'overview_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.overview.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_overview',
            },
            {
              handle: 'overview',
              title: this.$t('settings.project.projectType.themeSetting.navigation.overview.urlText'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.overview.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_overview',
            },
            {
              handle: 'overview_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.overview_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.overview_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_overview',
            },
            {
              handle: 'activate_imprint',
              title: this.$t('settings.project.projectType.themeSetting.navigation.imprint.title'),
              desc: null,
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.imprint.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'imprint_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.imprint.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_imprint',
            },
            {
              handle: 'imprint',
              title: this.$t('settings.project.projectType.themeSetting.navigation.imprint.urlText'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.imprint.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_imprint',
            },
            {
              handle: 'imprint_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.imprint_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.imprint_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_imprint',
            },
            {
              handle: 'activate_dataprivacy',
              title: this.$t('settings.project.projectType.themeSetting.navigation.dataprivacy.title'),
              desc: null,
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.dataprivacy.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'dataprivacy_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.dataprivacy.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_dataprivacy',
            },
            {
              handle: 'dataprivacy',
              title: this.$t('settings.project.projectType.themeSetting.navigation.dataprivacy.urlText'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.dataprivacy.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_dataprivacy',
            },
            {
              handle: 'dataprivacy_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.dataprivacy_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.dataprivacy_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_dataprivacy',
            },
            {
              handle: 'activate_custom1',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom1.title'),
              desc: null,
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.custom1.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'custom1_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom1.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_custom1',
            },
            {
              handle: 'custom1',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom1.urlText'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.custom1.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_custom1',
              showIfNotActive: 'menuButtons.custom1_openInModal',
            },
            {
              handle: 'custom1_openInModal',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom1_openInModal.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.custom1_openInModal.desc'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_custom1',
            },
            {
              handle: 'custom1',
              desc: this.$t('settings.project.projectType.themeSetting.navigation.custom1_internalPage.desc'),
              emptyText: this.$t('settings.project.projectType.themeSetting.navigation.custom1_internalPage.empty'),
              selectText: this.$t('settings.project.projectType.themeSetting.navigation.custom1_internalPage.select'),
              fieldType: 'InternalPageSelectRow',
              showIfActive: ['menuButtons.activate_custom1', 'menuButtons.custom1_openInModal'],
            },
            {
              handle: 'custom1_modalSize',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom1_modalSize.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.custom1_modalSize.desc'),
              fieldType: 'SelectRow',
              options: [
                { value: null, text: this.$t('settings.project.projectType.themeSetting.navigation.custom1_modalSize.default') },
                { value: 'sm', text: this.$t('settings.project.projectType.themeSetting.navigation.custom1_modalSize.sm') },
                { value: 'xl', text: this.$t('settings.project.projectType.themeSetting.navigation.custom1_modalSize.xl') },
                { value: 'full', text: this.$t('settings.project.projectType.themeSetting.navigation.custom1_modalSize.full') },
              ],
              showIfActive: ['menuButtons.activate_custom1', 'menuButtons.custom1_openInModal'],
            },
            {
              handle: 'custom1_modalCentered',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom1_modalCentered.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.custom1_modalCentered.desc'),
              fieldType: 'CheckboxRow',
              showIfActive: ['menuButtons.activate_custom1', 'menuButtons.custom1_openInModal'],
            },
            {
              handle: 'custom1_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom1_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.custom1_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_custom1',
            },
            {
              handle: 'custom1_icon',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom1_icon.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.custom1_icon.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_custom1',
            },
            {
              handle: 'custom1_openInNewTab',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom1_openInNewTab.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.custom1_openInNewTab.desc'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_custom1',
              showIfNotActive: 'menuButtons.custom1_openInModal',
            },
            {
              handle: 'activate_custom2',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom2.title'),
              desc: null,
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.custom2.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'custom2_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom2.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_custom2',
            },
            {
              handle: 'custom2',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom2.urlText'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.custom2.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_custom2',
              showIfNotActive: 'menuButtons.custom2_openInModal',
            },
            {
              handle: 'custom2_openInModal',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom2_openInModal.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.custom2_openInModal.desc'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_custom2',
            },
            {
              handle: 'custom2',
              desc: this.$t('settings.project.projectType.themeSetting.navigation.custom2_internalPage.desc'),
              emptyText: this.$t('settings.project.projectType.themeSetting.navigation.custom2_internalPage.empty'),
              selectText: this.$t('settings.project.projectType.themeSetting.navigation.custom2_internalPage.select'),
              fieldType: 'InternalPageSelectRow',
              showIfActive: ['menuButtons.activate_custom2', 'menuButtons.custom2_openInModal'],
            },
            {
              handle: 'custom2_modalSize',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom2_modalSize.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.custom2_modalSize.desc'),
              fieldType: 'SelectRow',
              options: [
                { value: null, text: this.$t('settings.project.projectType.themeSetting.navigation.custom2_modalSize.default') },
                { value: 'sm', text: this.$t('settings.project.projectType.themeSetting.navigation.custom2_modalSize.sm') },
                { value: 'xl', text: this.$t('settings.project.projectType.themeSetting.navigation.custom2_modalSize.xl') },
                { value: 'full', text: this.$t('settings.project.projectType.themeSetting.navigation.custom2_modalSize.full') },
              ],
              showIfActive: ['menuButtons.activate_custom2', 'menuButtons.custom2_openInModal'],
            },
            {
              handle: 'custom2_modalCentered',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom2_modalCentered.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.custom2_modalCentered.desc'),
              fieldType: 'CheckboxRow',
              showIfActive: ['menuButtons.activate_custom2', 'menuButtons.custom2_openInModal'],
            },
            {
              handle: 'custom2_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom2_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.custom2_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_custom2',
            },
            {
              handle: 'custom2_icon',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom2_icon.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.custom2_icon.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_custom2',
            },
            {
              handle: 'custom2_openInNewTab',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom2_openInNewTab.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.custom2_openInNewTab.desc'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_custom2',
              showIfNotActive: 'menuButtons.custom2_openInModal',
            },
            {
              handle: 'custom_button_1',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom_button_1.title'),
              fieldType: 'CustomButtonRow',
            },
            {
              handle: 'custom_button_2',
              title: this.$t('settings.project.projectType.themeSetting.navigation.custom_button_2.title'),
              fieldType: 'CustomButtonRow',
            },
            {
              handle: 'activate_intro',
              title: this.$t('settings.project.projectType.themeSetting.navigation.intro.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.intro.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.intro.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'intro_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.intro.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_intro',
            },
            {
              handle: 'intro_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.intro_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.intro_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_intro',
            },
            {
              handle: 'activate_languageSwitcher',
              title: this.$t('settings.project.projectType.themeSetting.navigation.languageSwitcher.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.languageSwitcher.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.languageSwitcher.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'languageSwitcher_showOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.languageSwitcher.showOnMobile'),
              fieldType: 'CheckboxRow',
              showIfActive: 'menuButtons.activate_languageSwitcher',
            },
            {
              handle: 'languageSwitcher_tooltip',
              title: this.$t('settings.project.projectType.themeSetting.navigation.languageSwitcher_tooltip.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.languageSwitcher_tooltip.desc'),
              fieldType: 'InputRowNoLabel',
              showIfActive: 'menuButtons.activate_languageSwitcher',
            },
          ],
        },
        {
          title: 'Side Menu',
          handle: 'sideMenu',
          fields: [
            {
              handle: 'menu_icon',
              title: this.$t('settings.project.projectType.themeSetting.navigation.menu_icon.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.menu_icon.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.menu_icon.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'menu_index',
              title: this.$t('settings.project.projectType.themeSetting.navigation.menu_index.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.menu_index.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.menu_index.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
              showIfActive: 'sideMenu.menu_icon',
            },
            {
              handle: 'menu_show_keywords',
              title: this.$t('settings.project.projectType.themeSetting.navigation.menu_show_keywords.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.menu_show_keywords.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.menu_show_keywords.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
              showIfActive: 'sideMenu.menu_icon',
            },
            {
              handle: 'menu_show_author',
              title: this.$t('settings.project.projectType.themeSetting.navigation.menu_show_author.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.menu_show_author.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.menu_show_author.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'menu_logo',
              title: this.$t('settings.project.projectType.themeSetting.navigation.menu_logo.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.menu_logo.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.menu_logo.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
              showIfActive: 'sideMenu.menu_icon',
            },
            {
              handle: 'menu_hide_images',
              title: this.$t('settings.project.projectType.themeSetting.navigation.menu_hide_images.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.menu_hide_images.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.menu_hide_images.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
              showIfActive: 'sideMenu.menu_icon',
            },
            {
              handle: 'showTaggedSubArticles',
              title: this.$t('settings.project.projectType.themeSetting.navigation.showTaggedSubArticles.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.showTaggedSubArticles.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.showTaggedSubArticles.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
              showIfActive: 'sideMenu.menu_icon',
            },
          ],
        },
        {
          title: this.$t('settings.project.projectType.themeSetting.navigation.articleSettings.title'),
          handle: 'articleSettings',
          fields: [
            {
              handle: 'activate_width',
              title: this.$t('settings.project.projectType.themeSetting.navigation.activate_width.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.activate_width.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.activate_width.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'maxWidth',
              title: this.$t('settings.project.projectType.themeSetting.navigation.maxWidth.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.maxWidth.desc'),
              fieldType: 'InputRow',
              showIfActive: 'articleSettings.activate_width',
            },
            {
              handle: 'maxWidthBackgroundColor',
              title: this.$t('settings.project.projectType.themeSetting.navigation.maxWidthBackgroundColor.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.maxWidthBackgroundColor.desc'),
              fieldType: 'ColorPickerRow',
              showIfActive: 'articleSettings.activate_width',
            },
          ],
        },
        {
          title: this.$t('settings.project.projectType.themeSetting.navigation.navigation.title'),
          handle: 'navigationArrow',
          fields: [
            {
              handle: 'navigation',
              title: this.$t('settings.project.projectType.themeSetting.navigation.navigation.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.navigation.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.navigation.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'showNavigationOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.showNavigationOnMobile.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.showNavigationOnMobile.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.showNavigationOnMobile.activate'),
              fieldType: 'CheckboxRowWithLabel',
              showIfActive: 'navigationArrow.navigation',
            },
            {
              handle: 'navigation_hide',
              title: this.$t('settings.project.projectType.themeSetting.navigation.navigation_hide.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.navigation_hide.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.navigation_hide.activate'),
              fieldType: 'CheckboxRowWithLabel',
              showIfActive: 'navigationArrow.navigation',
            },
            {
              handle: 'navigation_position',
              title: this.$t('settings.project.projectType.themeSetting.navigation.navigation_position.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.navigation_position.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.navigation_position.activate'),
              fieldType: 'CheckboxRowWithLabel',
              showIfActive: 'navigationArrow.navigation',
            },
            {
              handle: 'showNavigationAtSides',
              title: this.$t('settings.project.projectType.themeSetting.navigation.showNavigationAtSides.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.showNavigationAtSides.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.showNavigationAtSides.activate'),
              fieldType: 'CheckboxRowWithLabel',
              showIfActive: 'navigationArrow.navigation',
            },
          ],
        },
        {
          title: 'Navigation Widget',
          handle: 'navigationWidget',
          fields: [
            {
              handle: 'activate_pagination',
              title: this.$t('settings.project.projectType.themeSetting.navigation.activate_pagination.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.activate_pagination.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.activate_pagination.activate'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'showPaginationDesktop',
              title: this.$t('settings.project.projectType.themeSetting.navigation.showPaginationDesktop.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.showPaginationDesktop.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.showPaginationDesktop.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
              showIfActive: 'navigationWidget.activate_pagination',
            },
            {
              handle: 'hidePaginationOnScroll',
              title: this.$t('settings.project.projectType.themeSetting.navigation.hidePaginationOnScroll.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.hidePaginationOnScroll.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.hidePaginationOnScroll.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
              showIfActive: 'navigationWidget.activate_pagination',
            },
          ],
        },
        {
          title: 'Bottom Navigation Bar',
          handle: 'bottomNavigationBar',
          fields: [
            {
              handle: 'showBottomNavigationBar',
              title: this.$t('settings.project.projectType.themeSetting.navigation.showBottomNavigationBar.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.showBottomNavigationBar.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.showBottomNavigationBar.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
            },
            {
              handle: 'useStepsMenu',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.useStepsMenu.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.useStepsMenu.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.useStepsMenu.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
              showIfActive: 'bottomNavigationBar.showBottomNavigationBar',
            },
            {
              handle: 'stepsDisplayMode',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsDisplayMode.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsDisplayMode.desc'),
              fieldType: 'SelectRow',
              options: [
                { value: 'dots', text: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsDisplayMode.dots') },
                { value: 'progress', text: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsDisplayMode.progress') },
              ],
              showIfActive: ['bottomNavigationBar.showBottomNavigationBar', 'bottomNavigationBar.useStepsMenu'],
            },
            {
              handle: 'stepsBackgroundColor',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsBackgroundColor.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsBackgroundColor.desc'),
              fieldType: 'ColorPickerRow',
              showIfActive: ['bottomNavigationBar.showBottomNavigationBar', 'bottomNavigationBar.useStepsMenu'],
            },
            {
              handle: 'stepsTrackColor',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsTrackColor.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsTrackColor.desc'),
              fieldType: 'ColorPickerRow',
              showIfActive: ['bottomNavigationBar.showBottomNavigationBar', 'bottomNavigationBar.useStepsMenu'],
            },
            {
              handle: 'stepsDotColor',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsDotColor.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsDotColor.desc'),
              fieldType: 'ColorPickerRow',
              showIfActive: ['bottomNavigationBar.showBottomNavigationBar', 'bottomNavigationBar.useStepsMenu'],
            },
            {
              handle: 'stepsActiveDotColor',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsActiveDotColor.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsActiveDotColor.desc'),
              fieldType: 'ColorPickerRow',
              showIfActive: ['bottomNavigationBar.showBottomNavigationBar', 'bottomNavigationBar.useStepsMenu'],
            },
            {
              handle: 'stepsArrowBackgroundColor',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsArrowBackgroundColor.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsArrowBackgroundColor.desc'),
              fieldType: 'ColorPickerRow',
              showIfActive: ['bottomNavigationBar.showBottomNavigationBar', 'bottomNavigationBar.useStepsMenu'],
            },
            {
              handle: 'stepsArrowIconColorPrev',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsArrowIconColorPrev.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsArrowIconColorPrev.desc'),
              fieldType: 'ColorPickerRow',
              showIfActive: ['bottomNavigationBar.showBottomNavigationBar', 'bottomNavigationBar.useStepsMenu'],
            },
            {
              handle: 'stepsArrowIconColorNext',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsArrowIconColorNext.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsArrowIconColorNext.desc'),
              fieldType: 'ColorPickerRow',
              showIfActive: ['bottomNavigationBar.showBottomNavigationBar', 'bottomNavigationBar.useStepsMenu'],
            },
            {
              handle: 'stepsArrowBorderColor',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsArrowBorderColor.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.stepsArrowBorderColor.desc'),
              fieldType: 'ColorPickerRow',
              showIfActive: ['bottomNavigationBar.showBottomNavigationBar', 'bottomNavigationBar.useStepsMenu'],
            },
            // {
            //   handle: '_preview',
            //   title: 'Preview',
            //   fieldType: 'BottomNavPreview',
            //   showIfActive: 'bottomNavigationBar.showBottomNavigationBar',
            // },
            {
              handle: 'backgroundColor',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.backgroundColor.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.backgroundColor.desc'),
              fieldType: 'ColorPickerRow',
              showIfActive: 'bottomNavigationBar.showBottomNavigationBar',
              showIfNotActive: 'bottomNavigationBar.useStepsMenu',
            },
            {
              handle: 'activeBackgroundColor',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.activeBackgroundColor.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.activeBackgroundColor.desc'),
              fieldType: 'ColorPickerRow',
              showIfActive: 'bottomNavigationBar.showBottomNavigationBar',
              showIfNotActive: 'bottomNavigationBar.useStepsMenu',
            },
            {
              handle: 'iconColor',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.iconColor.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.iconColor.desc'),
              fieldType: 'ColorPickerRow',
              showIfActive: 'bottomNavigationBar.showBottomNavigationBar',
              showIfNotActive: 'bottomNavigationBar.useStepsMenu',
            },
            {
              handle: 'activeIconColor',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.activeIconColor.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.activeIconColor.desc'),
              fieldType: 'ColorPickerRow',
              showIfActive: 'bottomNavigationBar.showBottomNavigationBar',
              showIfNotActive: 'bottomNavigationBar.useStepsMenu',
            },
            {
              handle: 'showOnDesktop',
              title: this.$t('settings.project.projectType.themeSetting.navigation.showOnDesktop.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.showOnDesktop.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.showOnDesktop.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
              showIfActive: 'bottomNavigationBar.showBottomNavigationBar',
            },
            {
              handle: 'desktopPosition',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.desktopPosition.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.desktopPosition.desc'),
              fieldType: 'SelectRow',
              options: [
                { value: 'right', text: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.desktopPosition.rightFloating') },
                { value: 'left', text: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.desktopPosition.leftFloating') },
                { value: 'bottom', text: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.desktopPosition.bottomMenu') },
              ],
              showIfActive: ['bottomNavigationBar.showBottomNavigationBar', 'bottomNavigationBar.showOnDesktop'],
              showIfNotActive: 'bottomNavigationBar.useStepsMenu',
            },
            {
              handle: 'styleVariant',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.styleVariant.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.styleVariant.desc'),
              fieldType: 'SelectRow',
              options: [
                { value: 'A', text: 'A' },
                { value: 'B', text: 'B' },
                { value: 'C', text: 'C' },
              ],
              showIfActive: 'bottomNavigationBar.showBottomNavigationBar',
              showIfNotActive: 'bottomNavigationBar.useStepsMenu',
            },
            {
              handle: 'showMoreButton',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.showMoreButton.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.showMoreButton.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.showMoreButton.activate'),
              fieldType: 'CheckboxRowWithLabel',
              showIfActive: 'bottomNavigationBar.showBottomNavigationBar',
              showIfNotActive: 'bottomNavigationBar.useStepsMenu',
            },
            {
              handle: 'showMoreButtonNumber',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.showMoreButtonNumber.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.showMoreButtonNumber.desc'),
              fieldType: 'InputRow',
              showIfActive: ['bottomNavigationBar.showBottomNavigationBar', 'bottomNavigationBar.showMoreButton'],
              showIfNotActive: 'bottomNavigationBar.useStepsMenu',
            },
            {
              handle: 'showMoreButtonNumberDesktop',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.showMoreButtonNumberDesktop.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.showMoreButtonNumberDesktop.desc'),
              fieldType: 'InputRow',
              showIfActive: ['bottomNavigationBar.showBottomNavigationBar', 'bottomNavigationBar.showMoreButton'],
              showIfNotActive: 'bottomNavigationBar.useStepsMenu',
            },
            {
              handle: 'showMoreButtonLabel',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.showMoreButtonLabel.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.showMoreButtonLabel.desc'),
              fieldType: 'InputRow',
              showIfActive: ['bottomNavigationBar.showBottomNavigationBar', 'bottomNavigationBar.showMoreButton'],
              showIfNotActive: 'bottomNavigationBar.useStepsMenu',
            },
            {
              handle: 'hideIconsOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.hideIconsOnMobile.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.hideIconsOnMobile.desc'),
              fieldType: 'CheckboxRow',
              showIfActive: 'bottomNavigationBar.showBottomNavigationBar',
              showIfNotActive: 'bottomNavigationBar.useStepsMenu',
            },
            {
              handle: 'hideLabelsOnMobile',
              title: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.hideLabelsOnMobile.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.bottomNavigationBar.hideLabelsOnMobile.desc'),
              fieldType: 'CheckboxRow',
              showIfActive: 'bottomNavigationBar.showBottomNavigationBar',
              showIfNotActive: 'bottomNavigationBar.useStepsMenu',
            },
            {
              handle: 'hideOnScroll',
              title: this.$t('settings.project.projectType.themeSetting.navigation.hideOnScroll.title'),
              desc: this.$t('settings.project.projectType.themeSetting.navigation.hideOnScroll.desc'),
              checkboxLabel: this.$t('settings.project.projectType.themeSetting.navigation.hideOnScroll.checkboxLabel'),
              fieldType: 'CheckboxRowWithLabel',
              showIfActive: 'bottomNavigationBar.showBottomNavigationBar',
            },
            {
              handle: 'data',
              title: this.$t('settings.project.projectType.themeSetting.navigation.navigation_links.title'),
              fieldType: 'NavigationLinks',
              showIfActive: 'bottomNavigationBar.showBottomNavigationBar',
              showIfNotActive: 'bottomNavigationBar.useStepsMenu',
            },
          ],
        },
      ],
      themeValues: {},
    };
  },
  created() {
    this.themeValues = cloneDeep(themeSettingsDirectTop2);
    // add the values to the data
    if (this.settingValues) {
      Object.entries(this.settingValues).forEach(([key, value]) => {
        if (this.themeValues.hasOwnProperty(key)) {
          if (typeof value === 'object' && value !== null) {
            // Recursively merge nested objects
            this.themeValues[key] = this.deepMerge(this.themeValues[key], value);
          } else {
            this.themeValues[key] = value;
          }
        }
        if (this.themeValues.hasOwnProperty(key)) {
          if (typeof value === 'object' && value !== null) {
            // Handle nested objects by merging them
            this.themeValues[key] = {
              ...this.themeValues[key],
              ...value,
            };
          } else {
            // Handle primitive values
            this.themeValues[key] = value;
          }
        }
      });
    }
    this.$emit('update-theme-settings', this.themeValues);
  },
  computed: {
    sektionen() {
      return this.nurV3 ? v3Sektionen(this.themeFieldConfig) : this.themeFieldConfig;
    },
  },
  methods: {
    scrollToSection(sectionIndex) {
      const element = document.getElementById(`section-${sectionIndex}`);
      if (element) {
        const offset = 120; // Offset for sticky header
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    },
    deepMerge(target, source) {
      const result = { ...target };

      Object.keys(source).forEach((key) => {
        if (typeof source[key] === 'object' && source[key] !== null && !Array.isArray(source[key])) {
          if (typeof result[key] === 'object' && result[key] !== null) {
            result[key] = this.deepMerge(result[key], source[key]);
          } else {
            result[key] = { ...source[key] };
          }
        } else {
          result[key] = source[key];
        }
      });

      return result;
    },
    isSecondLevelField(value) {
      // Only apply background to fields that:
      // 1. Have showIfActive
      // 2. showIfActive is an array with more than one condition
      // 3. Are not checkbox fields
      if (!value?.showIfActive) {
        return false;
      }

      const isArray = Array.isArray(value.showIfActive);
      const hasMultipleConditions = isArray && value.showIfActive.length > 1;
      const isNotCheckbox = value.fieldType !== 'CheckboxRow' && value.fieldType !== 'CheckboxRowWithLabel';

      return hasMultipleConditions && isNotCheckbox;
    },
    showComponent(value) {
      // Hide styleVariant if user is not admin
      if (value?.handle === 'styleVariant' && !this.userObject?.isSysAdmin) {
        return false;
      }

      // showIfNotActive: hide field when ANY of the listed paths is truthy
      if (value?.showIfNotActive) {
        const notConditions = Array.isArray(value.showIfNotActive)
          ? value.showIfNotActive
          : [value.showIfNotActive];
        const anyTruthy = notConditions.some((condition) => {
          const keys = condition.split('.');
          const entry = keys.reduce((acc, key) => ((acc && acc[key] !== undefined) ? acc[key] : undefined), this.themeValues);
          return entry === true || entry === 'true' || entry === 1;
        });
        if (anyTruthy) return false;
      }

      // Bestehende showIfActive Logik
      if (value?.showIfActive) {
        // Unterstützt sowohl String als auch Array von Strings
        const conditions = Array.isArray(value.showIfActive) ? value.showIfActive : [value.showIfActive];

        // Alle Bedingungen müssen erfüllt sein (AND-Logik)
        const allConditionsMet = conditions.every((condition) => {
          const keys = condition.split('.');
          const entryOfThemeValues = keys.reduce((acc, key) => ((acc && acc[key] !== undefined) ? acc[key] : undefined), this.themeValues);

          if (entryOfThemeValues === undefined) {
            return false;
          }

          // Explizite Prüfung auf true-Werte
          return (entryOfThemeValues === true || entryOfThemeValues === 'true' || entryOfThemeValues === 1);
        });

        return allConditionsMet;
      }
      return true;
    },
    isDisabled(value) {
      // Wenn menu_right aktiv ist, bestimmte Komponenten deaktivieren
      const menuRightValue = this.themeValues.menu?.menu_right;
      if (menuRightValue === true || menuRightValue === 'true' || menuRightValue === 1) {
        const disabledInRightMenu = [
          'menu.title',
          'menu.article',
          'menu.scrollMenu',
          'menu.hideNavOnScroll',
          'menu.hideNavScrollOnlyOnMobile',
          'menuButtons.custom_button_1',
          'menuButtons.custom_button_2',
          'navigationArrow.navigation_position',
        ];

        const currentField = `${value.handle}`;
        const currentSection = this.getCurrentSection(value);

        if (disabledInRightMenu.includes(`${currentSection}.${currentField}`)) {
          return true;
        }
      }
      return false;
    },
    getCurrentSection(value) {
      // Finde die aktuelle Sektion basierend auf dem value
      const section = this.themeFieldConfig.find((configSection) => configSection.fields.some((field) => field.handle === value.handle));
      return section ? section.handle : 'menu'; // Fallback
    },
    getThemeValue(sectionHandle, itemHandle) {
      return this.themeValues[sectionHandle][itemHandle];
    },
    getComponentValue(sectionHandle, themeConfigItem) {
      // For preview component, pass the entire section settings
      if (themeConfigItem.fieldType === 'BottomNavPreview') {
        return this.themeValues[sectionHandle] || {};
      }
      // For other components, return the specific field value
      return this.getThemeValue(sectionHandle, themeConfigItem.handle);
    },
    updateSettingValue(value, itemHandle, sectionHandle) {
      if (this.themeValues[sectionHandle]) {
        this.$set(this.themeValues[sectionHandle], itemHandle, value);
        this.$emit('update-theme-settings', this.themeValues);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.quick-nav-menu {
  background: #5b6064;
  padding: 12px;
  margin-bottom: 10px;
}

.quick-nav-menu-wrapper {
 top:66px;
 z-index:6;
}
.quick-nav-title {
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 10px;
}

.quick-nav-links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.quick-nav-link {
  display: inline-block;
    padding: 0px 3px;
    border-radius: 4px;
    color: #ffffff;
    text-decoration: none;
    font-size: 12px;
    -webkit-transition: all 0.2s ease;
    transition: all 0.2s ease;

  &:hover {
    background: #007bff;
    color: #ffffff;
    text-decoration: underline;
  }

  &:focus {
    outline: none;
  }
}

.section-anchor {
  scroll-margin-top: 100px;
}

.sticky-title {
  position: sticky;
  top: 66px;
  background: #44484c;
  z-index: 2;
}

.disabled-field {
  opacity: 0.5;
  pointer-events: none;

  ::v-deep label {
    color: #999 !important;
  }

  ::v-deep input,
  ::v-deep select,
  ::v-deep textarea {
    background-color: #f5f5f5 !important;
    color: #999 !important;
    cursor: not-allowed !important;
  }

  ::v-deep .custom-control-label {
    color: #999 !important;
    cursor: not-allowed !important;
  }

  ::v-deep .custom-control-input:disabled ~ .custom-control-label::before {
    background-color: #e9ecef !important;
    border-color: #dee2e6 !important;
  }
}

.conditional-field {
  background-color: #404448;
}
.conditional-field > div{
  padding: 10px;
}
</style>
